import DownloadOva from '@bumble/app/components/DownloadOva';
import GenerateCloudData from '@bumble/app/components/GenerateCloudData';
import HardwareDevice from '@bumble/app/components/HardwareDevice';
import HostedNode from '@bumble/app/components/HostedNode';
import { Loading } from '@bumble/app/components/Loading';
import NodeComplete from '@bumble/app/components/NodeComplete';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';

const formFactorOptions = [
  {
    label: 'Download OVA',
    value: 'download',
  },
  {
    label: 'Hardware Device',
    value: 'hardware',
  },
  {
    label: 'AWS AMI',
    value: 'aws',
  },
  {
    label: 'Hosted Node',
    value: 'hosted',
  },
  {
    label: 'Skip for now',
    value: 'skip',
  },
];

interface ServiceNodeCreateProps {
  onCancel: () => void;
  showExisting?: boolean;
}
export const ServiceNodeCreate = ({ onCancel, showExisting = true }: ServiceNodeCreateProps) => {
  const api = useApi();

  const [activeStep, setActiveStep] = React.useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrev = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [serviceGroups, setServiceGroups] = useState('new');
  const handleLocationChange = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setServiceGroups(value);
  };

  const [serviceNodeName, setServiceNodeName] = React.useState('');
  const [serviceNodeGroup, setServiceNodeGroup] = React.useState('');
  const [selectedServiceNodeGroup, setSelectedServiceNodeGroup] = React.useState('');
  const [selectedNode, setSelectedNode] = React.useState('');
  const [selectedFormFactor, setSelectedFormFactor] = React.useState(formFactorOptions[3].value);

  const snPost = useMutation(api.createServiceNodeApiV1CreateServiceNodePost, {
    onSuccess(data) {
      toast.success('Service Node created.');
      setSelectedNode(data?.data?.service_node_id);
      handleNext();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    if (serviceGroups === 'new') {
      snPost.mutate({
        service_node_name: serviceNodeName,
        service_node_group_name: serviceNodeGroup,
        new_service_node_group: true,
      });
    } else {
      snPost.mutate({
        service_node_name: serviceNodeName,
        service_node_group_id: selectedServiceNodeGroup,
        new_service_node_group: false,
      });
    }
  };

  const handleCancel = () => {
    setSelectedNode('');
    onCancel();
  };

  const { isLoading: groupIsLoading, data: groupData } = useQuery(
    ['serviceNodeGroups'],
    api.listServiceNodeGroupApiV1ListServiceNodeGroupGet.bind(null, {}),
    {
      enabled: true,
      refetchOnMount: true, // To ensure service node group info is current
    },
  );

  if (groupIsLoading) {
    return <Loading />;
  }

  return (
    <Box pt={2}>
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Create Service Node</StepLabel>
          <StepContent>
            <Box>
              <TextField
                fullWidth
                id="sn-name"
                label="Service Node Name"
                variant="outlined"
                sx={{ mb: 4 }}
                value={serviceNodeName}
                onChange={(e) => setServiceNodeName(e.target.value)}
                autoFocus
              />
              {showExisting && groupData !== undefined && groupData?.data.length > 0 && (
                <>
                  <FormControl fullWidth sx={{ mb: 4 }}>
                    <FormLabel id="app-service-location">Service Node Group</FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="app-service-location"
                      name="app-service-group"
                      onChange={handleLocationChange}
                      value={serviceGroups}
                    >
                      <FormControlLabel value="new" control={<Radio />} label="New Group" />
                      <FormControlLabel value="existing" control={<Radio />} label="Use Existing" />
                    </RadioGroup>
                  </FormControl>
                  <Collapse in={serviceGroups === 'existing'} timeout="auto">
                    <FormControl fullWidth sx={{ mt: 4, mb: 2 }}>
                      <InputLabel id="node-groups-label">Service Node Groups</InputLabel>
                      <Select
                        labelId="node-groups-label"
                        id="node_groups"
                        label="Service Node Groups"
                        value={selectedServiceNodeGroup}
                        onChange={(e) => setSelectedServiceNodeGroup(e.target.value)}
                      >
                        {groupData.data.map(
                          ({ service_node_group_name, service_node_group_id }) => (
                            <MenuItem value={service_node_group_id} key={service_node_group_id}>
                              {service_node_group_name}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                  </Collapse>
                </>
              )}
              <Collapse in={serviceGroups === 'new'} timeout="auto">
                <TextField
                  fullWidth
                  id="sng-name"
                  label="Service Node Group Name"
                  variant="outlined"
                  sx={{ mb: 4 }}
                  value={serviceNodeGroup}
                  onChange={(e) => setServiceNodeGroup(e.target.value)}
                />
              </Collapse>
              <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
                <Button variant="outlined" onClick={handleSubmit}>
                  Create Service Node
                </Button>
              </Box>
            </Box>
          </StepContent>
        </Step>
        <Step>
          <StepLabel>Node Form Factor</StepLabel>
          <StepContent>
            <>
              <FormControl fullWidth sx={{ mb: 4 }}>
                <FormLabel id="node-form">Select an option</FormLabel>
                <RadioGroup
                  aria-labelledby="node-form"
                  name="nodeform"
                  onChange={(e) => setSelectedFormFactor(e.target.value)}
                  value={selectedFormFactor}
                >
                  {formFactorOptions.map(({ label, value }) => (
                    <FormControlLabel value={value} control={<Radio />} label={label} key={value} />
                  ))}
                </RadioGroup>
              </FormControl>
              <Button variant="outlined" onClick={handleNext}>
                Continue
              </Button>
            </>
          </StepContent>
        </Step>
        <Step>
          <StepLabel>Finish {selectedFormFactor !== 'skip' && `(${formFactorOptions.filter((f) => f.value === selectedFormFactor)[0].label})`}</StepLabel>
          <StepContent>
            {selectedFormFactor === 'download' && selectedNode !== '' && (
              <DownloadOva
                type="service"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'hardware' && selectedNode !== '' && (
              <HardwareDevice
                type="service"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'aws' && selectedNode !== '' && (
              <GenerateCloudData
                type="service"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'hosted' && selectedNode !== '' && (
              <HostedNode
                type="service"
                node={selectedNode}
                onClose={handleCancel}
                secondaryBtn={handlePrev}
              />
            )}
            {selectedFormFactor === 'skip' && (
              <NodeComplete onClose={handleCancel} secondaryBtn={handlePrev} />
            )}
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};
